import type { CcpPlan, CompetitorPlan, FieldCitation, FieldValue, PlanSource } from "../../types"

import { CCP_POTENTIAL_LINES } from "../ccp/ccp-constants"

/** SessionStorage key for the Document/Canvas view-mode preference (session-only). */
export const PLAN_VIEW_MODE_STORAGE_KEY = "sa-plan-view-mode"

export type PlanViewMode = "document" | "canvas"

/** Normalised section descriptor consumed by the generic Document/Canvas views. */
export type PlanSectionView = {
  no: string
  title: string
  fieldKey: string
  /** AI-updatable (Update button shown). False for the manual §06 tile. */
  updatable: boolean
  colSpan: number
  rowSpan: number
}

/** A single field paired with a human label — used for NS-badge lists and data gaps. */
export type LabelledField = { label: string; field: FieldValue }

export function fieldToText(field: FieldValue | undefined): string {
  if (!field) return ""
  return Array.isArray(field.value) ? field.value.filter(Boolean).join("\n") : field.value
}

export function fieldToLines(field: FieldValue | undefined): string[] {
  if (!field) return []
  if (Array.isArray(field.value)) return field.value.filter(Boolean)
  return field.value ? field.value.split("\n").filter(Boolean) : []
}

export function isNs(field: FieldValue | undefined): boolean {
  return field?.status === "not_specified"
}

export function fieldIsEmpty(field: FieldValue | undefined): boolean {
  if (!field) return true
  return Array.isArray(field.value) ? field.value.length === 0 : field.value.trim() === ""
}

/** ISO string → locale date; falls back to the raw string. */
export function formatPlanDate(iso: string | undefined): string {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

/** Flatten every FieldValue in a CCP plan with a display label (for gaps / NS scan). */
export function ccpLabelledFields(plan: CcpPlan): LabelledField[] {
  const s = plan.sections
  const out: LabelledField[] = [
    { label: "Customer overview", field: s.overview },
    { label: "Buying behavior", field: s.buying.note },
    { label: "Customers market overview", field: s.market },
    { label: "Key players", field: s.players },
    { label: "Customer business model", field: s.model },
    { label: "Business engagement", field: s.engage.note },
    { label: "Customer needs & pain points", field: s.needs },
    { label: "Value proposition", field: s.value },
    { label: "Key activities", field: s.activities },
  ]
  for (const [role, field] of Object.entries(s.buying.roles)) {
    out.push({ label: `Buying role — ${role}`, field })
  }
  for (const line of CCP_POTENTIAL_LINES) {
    out.push({ label: `Potential — ${line.label}`, field: s.potential[line.key] })
  }
  for (const item of s.engage.items) {
    out.push({ label: `Engagement — ${item.label}`, field: item.value })
  }
  if (s.engage.extra) out.push({ label: "Engagement — additional", field: s.engage.extra })
  return out
}

export function competitorLabelledFields(plan: CompetitorPlan): LabelledField[] {
  const s = plan.sections
  const out: LabelledField[] = [
    { label: "Competitor overview", field: s.overview },
    { label: "Business model", field: s.businessModel },
    { label: "Market overview", field: s.marketOverview },
    { label: "Key activities", field: s.keyActivities },
  ]
  s.strengthsWeaknesses.strengths.forEach((field, i) =>
    out.push({ label: `Strength #${i + 1}`, field })
  )
  s.strengthsWeaknesses.weaknesses.forEach((field, i) =>
    out.push({ label: `Weakness #${i + 1}`, field })
  )
  return out
}

/** Labels of every sub-field that came back Not specified (drives the data-gaps callout). */
export function dataGapLabels(fields: LabelledField[]): string[] {
  return fields.filter((entry) => isNs(entry.field)).map((entry) => entry.label)
}

/**
 * A plan is "generated" once it has been generated at least once — detected via
 * `generatedAt` or a non-zero `version`. `createdAt` is NOT used: an empty scaffold
 * always carries a creation stamp, so keying off it would false-positive.
 */
export function planIsGenerated(plan: {
  meta: { generatedAt?: string | null; version?: number }
}): boolean {
  return Boolean(plan.meta.generatedAt) || (plan.meta.version ?? 0) > 0
}

/** True when a generated plan is mostly Not specified (thin-evidence empty-state copy). */
export function planIsMostlyNs(fields: LabelledField[]): boolean {
  if (fields.length === 0) return false
  const nsCount = fields.filter((entry) => isNs(entry.field) || fieldIsEmpty(entry.field)).length
  return nsCount / fields.length >= 0.6
}

/** Slugify a citation index/label into a DOM id target for scroll+flash. */
export function citationAnchorId(planKind: string, fieldKey: string, index: number): string {
  return `sa-source-${planKind}-${fieldKey}-${index}`
}

/**
 * 1-based position of a citation's source in the (globally deduplicated) source
 * list, for scroll+flash targeting.
 *
 * Resolution is by identity only — `sourceId`, then `url`, then an unambiguous
 * `title`. A citation's own `index` is deliberately never trusted: it numbers
 * the citations of one field, while the source list is deduplicated across the
 * whole plan, so the two orderings do not correspond. Trusting it sent chip
 * `[1]` labelled "Executive summary" to a source that was actually "Existing
 * key customers" (visual E2E, review P1).
 *
 * A `title` match only counts when exactly one source carries that title;
 * duplicates are ambiguous and resolve to nothing. Returns `null` when the
 * cited source cannot be identified, so the caller leaves the chip inert rather
 * than sending the reader somewhere wrong.
 */
export function resolveCitationIndex(
  citation: FieldCitation,
  sources: PlanSource[]
): number | null {
  const byIdentity = sources.findIndex((source) => {
    if (
      citation.sourceId &&
      (source.sourceId === citation.sourceId || source.id === citation.sourceId)
    ) {
      return true
    }
    return Boolean(citation.url) && source.url === citation.url
  })
  if (byIdentity >= 0) return byIdentity + 1

  // Legacy fallback: older citations carry only a title.
  const title = citation.title?.trim()
  if (!title) return null
  const matches = sources.filter((source) => source.title?.trim() === title)
  if (matches.length !== 1) return null
  return sources.indexOf(matches[0]) + 1
}
